import useErrorMessage from 'Components/ContractGenerator/shared/helpers/useErrorMessage';
import useValidation from 'Components/ContractGenerator/shared/helpers/useValidation';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { appSelectors } from 'Redux/ducks/app';
import { contractGeneratorOperations, contractGeneratorSelectors } from 'Redux/ducks/contractGenerator';
import selectorStateComparison from 'Redux/utilities/selectorStateComparison';
import UserActionFeedback from 'ViatoUi/Shared/Feedback/UserActionFeedback';
import ValidationError from 'ViatoUi/Shared/Feedback/ValidationError';
import Spacer from 'ViatoUi/Shared/Layouts/Spacer';
import Paragraph from 'ViatoUi/Shared/Typo/Paragraph';
import DimmerPopup from 'ViatoUi/Web/DimmerPopup';
import Button from 'ViatoUi/Web/FormElements/Buttons/Button';

import Form from '../../../../Step2/ContractData/Form';
import styles from './EnableStep2.module.scss';

const EnableStep2 = ({ showPopup = false, setShowPopup, onSuccess }) => {
	const { t } = useTranslation('contractGenerator');
	const dispatch = useDispatch();
	const dataToSubmit = useSelector(contractGeneratorSelectors.getDataToSubmit);
	const fieldInfo = useSelector(contractGeneratorSelectors.getFieldInfo);
	const formFields = useSelector(contractGeneratorSelectors.getFormFields);
	const forms = useSelector(contractGeneratorSelectors.getEnableStep2Form);
	const { validationErrors } = useValidation();
	const [showValidationPopup, setShowValidationPopup] = useState(false);
	const language = useSelector(appSelectors.getLanguage);

	const [apiPending, apiError, apiErrorCode, apiErrorMessage] = useSelector(
		contractGeneratorSelectors.getActivateStep2ApiCall(),
		(formerState, currentState) => selectorStateComparison(formerState, currentState)
	);

	const errorMessage = useErrorMessage(apiErrorMessage);

	const formsToRender = useMemo(() => {
		const returnArray = [];

		forms.forEach(form => {
			let compareValue = get(dataToSubmit, form.conditions.field, '');
			if (typeof compareValue === 'object') {
				compareValue = compareValue.key;
			}

			if (!form.conditions.isConditional || (form.conditions.isConditional && compareValue === form.conditions.needsToBeValue)) {
				returnArray.push(form.name);
			}
		});

		return returnArray;
	}, [forms, dataToSubmit]);

	const { formName = '', insertValuesToForm = '' } = useMemo(() => {
		let name = '';
		let insertToForm = '';
		forms.forEach(form => {
			name = form.name;
			insertToForm = form.insertValuesToForm;
		});

		return { formName: name, insertValuesToForm: insertToForm };
	}, [forms]);

	useEffect(() => {
		if (showPopup && !showValidationPopup) {
			document.body.classList.add('scrolling-disabled');
		}
	}, [showValidationPopup]);

	return (
		<div className={styles.enable}>
			<DimmerPopup
				footerContent={
					<div className={styles.toolbar}>
						<Button
							type='dark'
							onClick={() => {
								setShowPopup(!showPopup);
							}}
						>
							{t('app.userActions.close')}
						</Button>
						<Button
							type='primary'
							onClick={() => {
								if (validationErrors.length > 0) {
									setShowValidationPopup(true);
								} else {
									dispatch(
										contractGeneratorOperations.setDataToSubmit({
											formData: { [insertValuesToForm]: { ...dataToSubmit.formData?.[formName] } }
										})
									);
									dispatch(
										contractGeneratorOperations.activateStep2({ formData: { ...dataToSubmit.formData }, language })
									);
								}
							}}
						>
							{t('pages.step1.products.area.enableStep2Form.button')}
						</Button>
					</div>
				}
				headline={t('pages.step1.products.area.enableStep2Form.headline')}
				visible={showPopup}
			>
				{t('pages.step1.products.area.enableStep2Form.text')}
				{formsToRender.map(form => (
					<Form
						key={form}
						fieldInfo={get(fieldInfo, `formData.${form}`, {})}
						formData={get(dataToSubmit, `formData.${form}`, {})}
						formFields={get(formFields, form, [])}
						formName={form}
						headline={t(`translation:forms.${form}.formName`)}
						showHeadline={false}
						styling={{ spacing: 's' }}
					/>
				))}
				<Spacer height='l' />
				<Paragraph font='normal'>{t('pages.step1.products.area.enableStep2Form.privacy')}</Paragraph>
			</DimmerPopup>

			<ValidationError setShowPopup={setShowValidationPopup} showPopup={showValidationPopup} />

			<UserActionFeedback
				error={!!apiError}
				errorCode={apiErrorCode}
				errorMessage={apiErrorMessage}
				pending={!!apiPending}
				type='modal'
				userMessage={errorMessage}
				isSilent
				showContractLoader
				onSuccess={onSuccess}
			/>
		</div>
	);
};

EnableStep2.propTypes = {
	showPopup: PropTypes.bool.isRequired,
	setShowPopup: PropTypes.func.isRequired,
	onSuccess: PropTypes.func.isRequired
};

export default EnableStep2;
